import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState} from "react";
import {cmpClassName} from "../../../common/generic-react.functions.js";
import Checkbox from "../../atoms/checkbox/checkbox.component.js";
import Label from "../../atoms/label/label.component.js";
import {useGroupGenerator, useMainGenerator} from "./checkbox-group.hooks.js";
import {CheckboxGroupContainer, CheckboxGroupContentContainer, CheckboxGroupContentItemsContainer, CheckboxGroupSelectAllContainer} from "./checkbox-group.styles.js";
function MainRenderGroup({
  title,
  contents,
  onChange
}, ref) {
  const [contentsState, setContentsState] = useState(contents);
  const {
    generatedContent,
    refs,
    isAllChecked: [isAllChecked]
  } = useGroupGenerator({
    contents: contentsState,
    title,
    onChange,
    setContentsState
  });
  const getRefs = useCallback(() => refs, [refs]);
  useImperativeHandle(ref, () => {
    return {
      getRefs
    };
  }, [getRefs]);
  useEffect(() => {
    setContentsState(contents);
  }, [contents]);
  const selectAllAction = useCallback((checked) => {
    const newContents = contentsState.map((content) => {
      return {
        ...content,
        checked
      };
    });
    setContentsState(newContents);
    onChange({
      title,
      contents: newContents
    });
  }, [contentsState, onChange, title]);
  return /* @__PURE__ */ React.createElement(CheckboxGroupContentItemsContainer, null, /* @__PURE__ */ React.createElement(Label, {
    tag: "div",
    bold: "semi"
  }, title), /* @__PURE__ */ React.createElement("div", null, contentsState.length > 1 && /* @__PURE__ */ React.createElement(Checkbox, {
    checked: isAllChecked,
    bold: "medium",
    onChange: selectAllAction
  }, "Select All"), generatedContent));
}
function MainCheckboxGroup({
  selectAll,
  items
}, ref) {
  const [itemsState, setItemsState] = useState(items);
  useEffect(() => {
    setItemsState(items);
  }, [items]);
  const {
    generatedContent,
    refs,
    isAllChecked: [isAllChecked, setIsAllChecked]
  } = useMainGenerator({
    items: itemsState,
    setItemsState
  });
  const getRefs = useCallback(() => refs.filter((r) => r.current).flatMap((r) => r.current?.getRefs()), [refs]);
  const getValue = useMemo(() => getRefs().map((r) => r?.current?.value), [getRefs]);
  const getItems = useCallback(() => itemsState, [itemsState]);
  const getSelected = useCallback(() => itemsState.map((item) => {
    return {
      ...item,
      contents: item.contents.filter(({
        checked
      }) => checked)
    };
  }), [itemsState]);
  const getUnSelected = useCallback(() => itemsState.map((item) => {
    return {
      ...item,
      contents: item.contents.filter(({
        checked
      }) => !checked)
    };
  }), [itemsState]);
  useImperativeHandle(ref, () => {
    return {
      el: null,
      valid: () => true,
      setValidateOnChange: () => void 0,
      value: getValue,
      getRefs,
      getItems,
      getSelected,
      getUnSelected
    };
  }, [getItems, getRefs, getSelected, getUnSelected, getValue]);
  const selectAllAction = useCallback((checked) => {
    const newItemsState = itemsState.map((it) => {
      const newContents = it.contents.map((content) => ({
        ...content,
        checked
      }));
      return {
        ...it,
        contents: newContents
      };
    });
    setIsAllChecked(checked);
    setItemsState(newItemsState);
  }, [itemsState, setIsAllChecked]);
  return /* @__PURE__ */ React.createElement(CheckboxGroupContainer, {
    className: cmpClassName("checkbox-group")
  }, selectAll && /* @__PURE__ */ React.createElement(CheckboxGroupSelectAllContainer, null, /* @__PURE__ */ React.createElement(Checkbox, {
    bold: "semi",
    checked: isAllChecked,
    onChange: selectAllAction
  }, selectAll)), /* @__PURE__ */ React.createElement(CheckboxGroupContentContainer, null, generatedContent));
}
const CheckboxGroup = /* @__PURE__ */ React.memo(/* @__PURE__ */ forwardRef(MainCheckboxGroup));
CheckboxGroup.displayName = "CheckboxGroup";
export default CheckboxGroup;
const RenderGroup = /* @__PURE__ */ React.memo(/* @__PURE__ */ forwardRef(MainRenderGroup));
RenderGroup.displayName = "RenderGroup";
export {RenderGroup};
